import _ from 'lodash';

import {
    CanJoinBeforeInstructor,
    type ClassSessionGroupPayload,
    type MeetingObj,
    type MeetingSettings,
    RequireAuth,
    WaitingRoom
} from './types';
import { AttendanceType, type ClassSessionWithGroupData } from '../../components/addSessions/types';
import { type ClassRecordData } from '../../context/types';

const returnDelta = <T>(object1: any, object2: any): T => {
    const delta = {};

    Object.keys(object1).forEach((key) => {
        if (!_.isEqual(object1[key], object2[key])) {
            delta[key] = object2[key];
        }
    });

    return delta as T;
};

export const returnMeetingDataDelta = (initialData: any, formValues: any) => {
    const initialMeetingSettings = {
        join_before_host: initialData.canJoinBeforeInstructor === CanJoinBeforeInstructor.YES,
        waiting_room: initialData.waitingRoom === WaitingRoom.ON,
        meeting_authentication: initialData.requireAuth === RequireAuth.ON
    };

    const meetingSettings = {
        join_before_host: formValues.canJoinBeforeInstructor === CanJoinBeforeInstructor.YES,
        waiting_room: formValues.waitingRoom === WaitingRoom.ON,
        meeting_authentication: formValues.requireAuth === RequireAuth.ON
    };

    const meetingSettingsDelta: Partial<MeetingSettings> = returnDelta(initialMeetingSettings, meetingSettings);

    const initialMeetingData: Partial<MeetingObj> = {
        topic: initialData.className,
        password: initialData.classPasscode,
        agenda: initialData.description
    };

    const meetingData: Partial<MeetingObj> = {
        topic: formValues.className,
        password: formValues.classPasscode,
        agenda: formValues.description
    };

    const meetingDelta: Partial<MeetingObj> = returnDelta(initialMeetingData, meetingData);

    if (!_.isEmpty(meetingSettingsDelta)) {
        meetingDelta.settings = meetingSettingsDelta;
    }

    return meetingDelta;
};

export const returnClassDataDelta = (initialData: any, formValues: any): ClassRecordData => {
    const initialClassData: ClassRecordData = {
        class_name: initialData.className,
        class_description: initialData.description,
        class_code: initialData.classId,
        content_type: initialData.contentType,
        timezone: initialData.timezone,
        class_times: initialData.class_times
    };

    const classData: ClassRecordData = {
        class_name: formValues.className,
        class_description: formValues.description,
        class_code: formValues.classId,
        content_type: formValues.contentType,
        timezone: formValues.timezone,
        class_times: formValues.class_times
    };

    return returnDelta<ClassRecordData>(initialClassData, classData);
};

export const groupSessions = (sessions: ClassSessionWithGroupData[]): ClassSessionGroupPayload[] => {
    const classSessionGroupsPayload: { [sessionGroupUuid: string]: ClassSessionGroupPayload } = {};

    sessions.forEach((session: ClassSessionWithGroupData) => {
        if (!session.classSessionGroupUuid) return;
        if (!classSessionGroupsPayload[session.classSessionGroupUuid]) {
            let trackAttendance = false;
            let attendanceLmsPush = false;
            switch (session.attendanceType) {
                case AttendanceType.Native:
                    trackAttendance = true;
                    break;
                case AttendanceType.IntegrateWithLms:
                    trackAttendance = true;
                    attendanceLmsPush = true;
                    break;
                default:
                    break;
            }

            classSessionGroupsPayload[session.classSessionGroupUuid] = {
                class_session_group_uuid: session.classSessionGroupUuid,
                schedule: {
                    class_times: []
                },
                config: {
                    label: session.label,
                    selectedGroups: session.selectedGroups,
                    trackAttendance,
                    attendanceLmsPush
                }
            } as ClassSessionGroupPayload;
        }
        classSessionGroupsPayload[session.classSessionGroupUuid].schedule.class_times.push(
            `${session.start_time}:${session.end_time}`
        );
    });

    return Object.values(classSessionGroupsPayload);
};

export const datesHaveBeenUpdated = (dirtyFields: any) => {
    if (!dirtyFields.dates) {
        return false;
    }

    return dirtyFields.dates.some((date) => {
        if (!_.isEmpty(date)) {
            const keys = Object.keys(date);

            if (keys.length === 1 && keys[0] === 'overnightAcknowledged') {
                return false;
            }
            return true;
        }
        return false;
    });
};
